import { SoundId } from './types';

/**
 * Sonidos sintetizados con WebAudio. No dependen de archivos, así que el
 * Browser Source de OBS no tiene que descargar nada ni esperar assets.
 */

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    if (!ctx) ctx = new Ctx();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

interface ToneOptions {
  freq: number;
  type?: OscillatorType;
  at?: number;
  duration?: number;
  gain?: number;
  attack?: number;
  filter?: number;
}

function tone(audio: AudioContext, master: GainNode, options: ToneOptions) {
  const {
    freq,
    type = 'sine',
    at = 0,
    duration = 0.6,
    gain = 0.2,
    attack = 0.01,
    filter,
  } = options;

  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const env = audio.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);

  env.gain.setValueAtTime(0.0001, start);
  env.gain.linearRampToValueAtTime(gain, start + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  let node: AudioNode = env;
  if (filter) {
    const lp = audio.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(filter, start);
    env.connect(lp);
    node = lp;
  }

  osc.connect(env);
  node.connect(master);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

function noiseSwell(audio: AudioContext, master: GainNode, at: number, duration: number, gain: number, cutoff: number) {
  const start = audio.currentTime + at;
  const frames = Math.floor(audio.sampleRate * duration);
  const buffer = audio.createBuffer(1, frames, audio.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    data[i] = (Math.random() * 2 - 1) * 0.6;
  }

  const source = audio.createBufferSource();
  source.buffer = buffer;

  const lp = audio.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(cutoff, start);

  const env = audio.createGain();
  env.gain.setValueAtTime(0.0001, start);
  env.gain.linearRampToValueAtTime(gain, start + duration * 0.45);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  source.connect(lp);
  lp.connect(env);
  env.connect(master);
  source.start(start);
  source.stop(start + duration);
}

const RECIPES: Record<Exclude<SoundId, 'none'>, (audio: AudioContext, master: GainNode) => void> = {
  chime: (audio, master) => {
    tone(audio, master, { freq: 523.25, at: 0, duration: 0.8, gain: 0.22 });
    tone(audio, master, { freq: 659.25, at: 0.18, duration: 0.9, gain: 0.18 });
  },

  bell: (audio, master) => {
    tone(audio, master, { freq: 880, type: 'triangle', at: 0, duration: 1.8, gain: 0.22, attack: 0.005 });
    tone(audio, master, { freq: 1318.5, type: 'triangle', at: 0, duration: 1.2, gain: 0.08, attack: 0.005 });
    tone(audio, master, { freq: 2637, type: 'sine', at: 0, duration: 0.5, gain: 0.04, attack: 0.003 });
  },

  marimba: (audio, master) => {
    [587.33, 783.99, 1046.5].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        type: 'sine',
        at: index * 0.11,
        duration: 0.45,
        gain: 0.24,
        attack: 0.004,
        filter: 2600,
      });
    });
  },

  air: (audio, master) => {
    noiseSwell(audio, master, 0, 1.9, 0.1, 900);
    tone(audio, master, { freq: 392, at: 0.1, duration: 1.6, gain: 0.06, attack: 0.5 });
  },

  bowl: (audio, master) => {
    tone(audio, master, { freq: 196, at: 0, duration: 3.2, gain: 0.2, attack: 0.08, filter: 1400 });
    tone(audio, master, { freq: 293.66, at: 0.05, duration: 2.8, gain: 0.1, attack: 0.1, filter: 1600 });
    tone(audio, master, { freq: 198.5, at: 0, duration: 3.0, gain: 0.07, attack: 0.12 });
  },

  blip: (audio, master) => {
    tone(audio, master, { freq: 880, type: 'square', at: 0, duration: 0.09, gain: 0.12, attack: 0.002 });
    tone(audio, master, { freq: 1320, type: 'square', at: 0.1, duration: 0.11, gain: 0.12, attack: 0.002 });
  },

  arcade: (audio, master) => {
    [440, 554.37, 659.25, 880].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        type: 'square',
        at: index * 0.07,
        duration: 0.1,
        gain: 0.1,
        attack: 0.002,
        filter: 3200,
      });
    });
  },

  gong: (audio, master) => {
    tone(audio, master, { freq: 110, type: 'sawtooth', at: 0, duration: 3.4, gain: 0.16, attack: 0.02, filter: 620 });
    tone(audio, master, { freq: 164.81, type: 'sine', at: 0, duration: 2.6, gain: 0.1, attack: 0.03 });
    noiseSwell(audio, master, 0, 0.5, 0.06, 500);
  },
};

export function playCoworkSound(id: SoundId | undefined, volume: number) {
  if (!id || id === 'none') return;
  const recipe = RECIPES[id as Exclude<SoundId, 'none'>];
  if (!recipe) return;

  const audio = getContext();
  if (!audio) return;

  try {
    const master = audio.createGain();
    master.gain.value = Math.max(0, Math.min(1, volume / 100));
    master.connect(audio.destination);
    recipe(audio, master);
  } catch {
    /* el overlay nunca debe romperse por el audio */
  }
}
